import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { fetchBanners, preloaded } from '../lib/api';
import { imageProps, resolveImage } from '../lib/images';

const defaultSlides = [
  {
    id: 'exaustores',
    badge: 'PROMOÇÃO ESPECIAL',
    title1: 'VENTILAÇÃO',
    title2: 'PERFEITA',
    subtitle: 'ITC FAN 100mm — Qualidade, Confiança e Desempenho',
    cta: 'VER EXAUSTORES',
    href: '#exaustores',
    bg: 'from-yellow-400 via-yellow-500 to-orange-400',
    badgeBg: 'bg-blue-700',
    ctaBg: 'bg-blue-700 hover:bg-blue-800',
    badgeText: 'ATÉ',
    badgeBig: '30%',
    badgeSmall: 'OFF',
    badgeColor: 'from-blue-800 to-blue-700',
    note: 'Mais qualidade. Melhor ventilação.',
  },
  {
    id: 'kits',
    badge: 'KITS COMPLETOS',
    title1: 'TUDO QUE',
    title2: 'VOCÊ PRECISA',
    subtitle: 'Exaustor + Duto + Grade no mesmo kit — instalação rápida',
    cta: 'VER KITS',
    href: '#kits',
    bg: 'from-blue-600 via-blue-700 to-cyan-600',
    badgeBg: 'bg-yellow-500',
    ctaBg: 'bg-orange-500 hover:bg-orange-600',
    badgeText: 'A PARTIR DE',
    badgeBig: 'R$110',
    badgeSmall: '6x sem juros',
    badgeColor: 'from-yellow-500 to-orange-500',
    note: 'Frete grátis acima de R$ 300.',
  },
  {
    id: 'pecas',
    badge: 'PEÇAS DE FOGÃO',
    title1: 'USINAS &',
    title2: 'REGISTROS',
    subtitle: 'Centelhadores e kits de registros — 2, 4 e 5 bocas disponíveis',
    cta: 'VER PEÇAS',
    href: '#pecas',
    bg: 'from-orange-500 via-red-500 to-pink-500',
    badgeBg: 'bg-white text-red-600',
    ctaBg: 'bg-gray-900 hover:bg-black',
    badgeText: 'COMPATIBILIDADE',
    badgeBig: '100%',
    badgeSmall: 'UNIVERSAL',
    badgeColor: 'from-white to-gray-100 text-red-600',
    note: 'Compatível com todos os fogões cooktop.',
  },
];

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [slides, setSlides] = useState(() => {
    const pre = preloaded('banners');
    return pre && pre.length > 0 ? pre : defaultSlides;
  });

  useEffect(() => {
    fetchBanners()
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setSlides(data);
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [slides.length]);

  const slide = slides[index] || slides[0];
  const isLightOnDark = slide.id !== 'exaustores';
  const hasImage = !!slide.image;
  const imgPos = slide.image_position || 'right';
  const imgFit = slide.image_object_fit || 'contain';
  const imgWidth = slide.image_width || '';
  const imgHeight = slide.image_height || '';
  const isBgImage = hasImage && imgPos === 'bg';
  const isImageOnly = hasImage && imgPos === 'full';

  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-br ${slide.bg} py-16 md:py-20 transition-all duration-700`}
      data-testid="hero-carousel"
    >
      {/* Optional background image */}
      {isBgImage && (
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage: `url(${resolveImage(slide.image)})`,
            backgroundSize: imgFit === 'cover' ? 'cover' : 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.6,
          }}
        />
      )}
      {/* Slides */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          key={slide.id}
          className={`flex flex-col md:flex-row items-center gap-8 animate-fade-in ${imgPos === 'left' ? 'md:flex-row-reverse' : ''}`}
          data-testid={`hero-slide-${slide.id}`}
        >
          {/* Left Content */}
          {!isImageOnly && (
            <div className={`flex-1 ${isLightOnDark ? 'text-white' : 'text-gray-900'}`}>
              <div className={`inline-block ${slide.badgeBg} text-white px-4 py-1 rounded-full text-sm font-bold mb-4`}>
                {slide.badge}
              </div>
              <h2 className="text-5xl md:text-7xl font-black mb-4 leading-tight">
                {slide.title1}
                <br />
                <span className={isLightOnDark ? 'drop-shadow-lg' : 'text-white drop-shadow-lg'}>
                  {slide.title2}
                </span>
              </h2>
              <p className={`text-xl md:text-2xl mb-8 ${isLightOnDark ? 'text-white/90' : 'text-gray-800'}`}>
                {slide.subtitle}
              </p>
              <a
                href={slide.href}
                className={`inline-flex items-center gap-2 ${slide.ctaBg} text-white font-bold py-4 px-8 rounded-full text-lg transition-all transform hover:scale-105 shadow-xl`}
                data-testid={`hero-cta-${slide.id}`}
              >
                {slide.cta}
                <ArrowRight size={24} />
              </a>
              <p className={`text-sm mt-4 ${isLightOnDark ? 'text-white/70' : 'text-gray-700'}`}>
                {slide.note}
              </p>
            </div>
          )}

          {/* Right Content — either custom image or default Promo Badge */}
          <div className={`${isImageOnly ? 'flex-1 w-full' : 'flex-1'} flex justify-center items-center`}>
            {hasImage && !isBgImage ? (
              <img
                {...imageProps(slide.image, { sizes: '(max-width: 768px) 100vw, 600px' })}
                alt={slide.title1 || 'Banner'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                loading="eager"
                decoding="async"
                width={slide.image_natural_width || undefined}
                height={slide.image_natural_height || undefined}
                style={{
                  width: imgWidth || (isImageOnly ? '100%' : 'auto'),
                  height: imgHeight || 'auto',
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: imgFit,
                }}
                className="rounded-2xl shadow-2xl"
                data-testid={`hero-image-${slide.id}`}
              />
            ) : !hasImage ? (
              <div className="relative">
                <div
                  className={`w-72 h-72 md:w-80 md:h-80 rounded-full bg-gradient-to-br ${slide.badgeColor} flex flex-col items-center justify-center shadow-2xl transform hover:scale-105 transition-transform`}
                >
                  <div className="text-center px-6">
                    <p className="text-3xl md:text-4xl font-black mb-1">{slide.badgeText}</p>
                    <p className="text-6xl md:text-7xl font-black mb-1 leading-none">
                      {slide.badgeBig}
                    </p>
                    <p className="text-3xl md:text-4xl font-black">{slide.badgeSmall}</p>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl p-4 shadow-xl max-w-[14rem]">
                  <p className="text-yellow-600 font-bold text-sm">Mais qualidade.</p>
                  <p className="text-yellow-600 font-bold text-sm">Melhor ventilação.</p>
                  <p className="text-blue-700 font-bold text-sm">Esta semana na ELETROVEX</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Dots indicator */}
        <div
          className="flex justify-center gap-2 mt-10"
          data-testid="hero-dots"
          role="tablist"
        >
          {slides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Slide ${i + 1}`}
              aria-selected={i === index}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>

      </div>

      <style>{`
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.7s ease-out;
        }
      `}</style>
    </section>
  );
};

export default Hero;
